import { CreditCard, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { theme } from "./TicketsPage";
import { useAuth } from "react-oidc-context";
import { Link, useParams } from "react-router";
import { apiSend, useApi } from "../api";
import type { PortalConfig } from "../config";
import { date, money, phone } from "../format";
import { confirmBox } from "../confirm";
import { cardLabel } from "./ticket/CardDialogs";

interface Row { id: string; firstName: string | null; lastName: string | null; email: string; phone: string; isWholesale: boolean; tickets: number; lastTicket: string | null }
interface Address { id: string; street: string; street2: string | null; poBox: string | null; city: string; state: string; zip: string; tickets: number }
interface Detail {
  customer: { id: string; firstName: string | null; lastName: string | null; email: string; phone: string; phoneIsMobile: boolean; isWholesale: boolean;
    allowEmail: boolean; allowText: boolean; allowShippingLabels: boolean; chargeSalesTax: boolean; createdAt: string };
  addresses: Address[];
  tickets: { ticketNo: number; createdAt: string; status: string; isClosed: boolean; brands: string | null; jobNumber: string | null; jobName: string | null; amount: string | null }[];
  cards: { id: string; cardType: string | null; last4: string | null; expiration: string | null; environment: string; createdAt: string }[];
  totalPaid: string;
}
const name = (c: { firstName: string | null; lastName: string | null; email: string }) => [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email;

/** Customers (owner, 2026-11-04): search by name, email or phone; wholesale only; most recent first. */
export function CustomersPage({ config }: { config: PortalConfig }) {
  const [q, setQ] = useState("");
  const [typed, setTyped] = useState("");
  const [wholesale, setWholesale] = useState(false);
  useEffect(() => { const h = setTimeout(() => setQ(typed), 300); return () => clearTimeout(h); }, [typed]);
  const { data, error, loading } = useApi<{ customers: Row[] }>(config, `/customers?list=1&q=${encodeURIComponent(q)}${wholesale ? "&wholesale=1" : ""}`);
  return (
    <div className="admin-page">
      <div className="admin-head">
        <h2>Customers</h2>
        <input type="search" placeholder="Search name, email or phone (3+ characters)" value={typed} onChange={(e) => setTyped(e.target.value)} aria-label="Search customers" />
        <label className="check"><input type="checkbox" checked={wholesale} onChange={(e) => setWholesale(e.target.checked)} /> Wholesale only</label>
      </div>
      {error && <p className="error">{error.message}</p>}
      <p className="hint">{loading ? "Loading…" : q.trim().length >= 3 ? `${data?.customers.length ?? 0} found` : "Most recent customers (up to 200). Search to find anyone."}</p>
      <table className="t-plain admin">
        <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th className="num">Tickets</th><th>Last ticket</th></tr></thead>
        <tbody>{data?.customers.map((c) => (
          <tr key={c.id}><td><Link to={`/customers/${c.id}`}><strong>{name(c)}</strong></Link>{c.isWholesale && <span className="tag">Wholesale</span>}</td>
            <td>{c.email}</td><td>{c.phone ? phone(c.phone) : ""}</td><td className="num">{c.tickets}</td><td>{c.lastTicket ? date(c.lastTicket) : "—"}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

const SWITCHES: [keyof Detail["customer"], string, string][] = [
  ["allowEmail", "Email OK", "Automatic emails (wholesale customers only get them when this is on)"],
  ["allowText", "Texts OK", "Automatic texts (also needs a mobile number)"],
  ["phoneIsMobile", "Mobile phone", "The phone can receive texts"],
  ["isWholesale", "Wholesale", "No return shipping line or labels by default; quick key on Add Ticket"],
  ["allowShippingLabels", "Shipping labels", "Add Ticket buys a free label and emails it"],
  ["chargeSalesTax", "Sales tax", "Wisconsin sales tax on Wisconsin addresses"],
];

/** One customer: details and switches, saved addresses, cards on file, tickets. */
export function CustomerPage({ config }: { config: PortalConfig }) {
  const { id = "" } = useParams();
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [reload, setReload] = useState(0);
  const { data, error } = useApi<Detail>(config, `/customers/${id}`, reload);
  const [edit, setEdit] = useState<Detail["customer"] | null>(null);
  const [addr, setAddr] = useState<{ id: string | null; street: string; street2: string; city: string; state: string; zip: string } | null>(null);
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (method: string, path: string, body?: unknown) => {
    setBusy(true); setProblem("");
    try { await apiSend(config, token, method, path, body); setReload((r) => r + 1); return true; }
    catch (e) { setProblem((e as Error).message); return false; } finally { setBusy(false); }
  };
  if (error) return <div className="admin-page"><p className="error">{error.message}</p></div>;
  if (!data) return <div className="admin-page"><p className="muted">Loading…</p></div>;
  const c = data.customer;
  const open = data.tickets.filter((t) => !t.isClosed).length;
  return (
    <div className="admin-page customer">
      <p><Link to="/customers">← Customers</Link></p>
      <div className="admin-head"><h2>{name(c)}</h2>{c.isWholesale && <span className="tag">Wholesale</span>}
        <span className="hint">Customer since {date(c.createdAt)} · {data.tickets.length} ticket{data.tickets.length === 1 ? "" : "s"}{open ? ` (${open} open)` : ""} · paid {money(data.totalPaid)}</span></div>
      {problem && <p className="error">{problem}</p>}
      <div className="cust-cols">
        <section className="panel">
          <div className="panel-head"><h3>Details</h3>{!edit && <button type="button" className="icon-link" aria-label="Edit customer" onClick={() => setEdit({ ...c })}><Pencil size={16} /></button>}</div>
          {edit ? (
            <form className="stack-form" onSubmit={async (e) => { e.preventDefault(); if (await run("PUT", `/customers/${id}`, edit)) setEdit(null); }}>
              <div className="two"><label>First name<input value={edit.firstName ?? ""} onChange={(e) => setEdit({ ...edit, firstName: e.target.value })} /></label>
                <label>Last name<input value={edit.lastName ?? ""} onChange={(e) => setEdit({ ...edit, lastName: e.target.value })} /></label></div>
              <label>Email<input type="email" required value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></label>
              <label>Phone<input type="tel" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></label>
              {SWITCHES.map(([k, l, hint]) => (
                <label key={k} className="check" title={hint}><input type="checkbox" checked={!!edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.checked })} /> {l} <span className="hint">· {hint}</span></label>
              ))}
              <p className="hint">Changes apply to all of this customer's tickets.</p>
              <div className="btns"><button type="submit" className="tb-btn primary" disabled={busy}>Save</button><button type="button" className="tb-btn" onClick={() => setEdit(null)}>Cancel</button></div>
            </form>
          ) : (
            <dl className="kv">
              <dt>Email</dt><dd><a href={`mailto:${c.email}`}>{c.email}</a></dd>
              <dt>Phone</dt><dd>{c.phone ? <a href={`tel:${c.phone}`}>{phone(c.phone)}</a> : "—"}</dd>
              {SWITCHES.map(([k, l]) => <Fragment2 key={k} label={l} on={!!c[k]} />)}
            </dl>
          )}
        </section>

        <section className="panel">
          <div className="panel-head"><h3><MapPin size={16} /> Saved addresses</h3>
            {!addr && <button type="button" className="icon-link" aria-label="Add address" onClick={() => setAddr({ id: null, street: "", street2: "", city: "", state: "", zip: "" })}><Plus size={16} /></button>}</div>
          {addr && (
            <form className="stack-form" onSubmit={async (e) => {
              e.preventDefault();
              const ok = addr.id ? await run("PUT", `/customers/${id}/addresses/${addr.id}`, addr) : await run("POST", `/customers/${id}/addresses`, addr);
              if (ok) setAddr(null);
            }}>
              <label>Street<input required value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })} autoFocus /></label>
              <label>Street 2<input value={addr.street2} onChange={(e) => setAddr({ ...addr, street2: e.target.value })} /></label>
              <div className="three"><label>City<input required value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} /></label>
                <label>State<input required value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} /></label>
                <label>Zip<input required value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} /></label></div>
              {addr.id && <p className="hint">Tickets that use this address show the change too.</p>}
              <div className="btns"><button type="submit" className="tb-btn primary" disabled={busy}>Save</button><button type="button" className="tb-btn" onClick={() => setAddr(null)}>Cancel</button></div>
            </form>
          )}
          <ul className="addr-list">
            {data.addresses.map((a) => (
              <li key={a.id}><div>{a.street}{a.street2 ? `, ${a.street2}` : ""}{a.poBox ? `, PO Box ${a.poBox}` : ""}<br />{a.city}, {a.state} {a.zip}
                <div className="hint">{a.tickets} ticket{a.tickets === 1 ? "" : "s"}</div></div>
                <span className="nowrap">
                  <button type="button" className="icon-link" aria-label="Edit address" onClick={() => setAddr({ id: a.id, street: a.street, street2: a.street2 ?? "", city: a.city, state: a.state, zip: a.zip })}><Pencil size={15} /></button>
                  <button type="button" className="icon-link danger" aria-label="Remove address" disabled={busy}
                    onClick={() => { void confirmBox("Remove this address from the customer's list? Tickets that already use it keep it.").then((ok) => { if (ok) void run("DELETE", `/customers/${id}/addresses/${a.id}`); }); }}><Trash2 size={15} /></button>
                </span></li>
            ))}
            {!data.addresses.length && <li className="hint">No saved addresses.</li>}
          </ul>

          <h3 className="sub-h"><CreditCard size={16} /> Cards on file</h3>
          <ul className="addr-list">
            {data.cards.map((k) => <li key={k.id}><div>{cardLabel(k)}{k.expiration && k.expiration !== "XXXX" ? ` · expires ${k.expiration}` : ""}
              <div className="hint">{k.environment === "production" ? "Old system (live Authorize.Net)" : "Sandbox (test)"} · added {date(k.createdAt)}</div></div></li>)}
            {!data.cards.length && <li className="hint">None. Cards are added on a ticket (View Payment Profile) or by the customer on their page.</li>}
          </ul>
        </section>
      </div>

      <CustomerTickets tickets={data.tickets} />
    </div>
  );
}

function Fragment2({ label, on }: { label: string; on: boolean }) {
  return <><dt>{label}</dt><dd className={on ? "on" : "off"}>{on ? "Yes" : "No"}</dd></>;
}

type TicketRowC = Detail["tickets"][number];

/** The customer's tickets: sortable, filterable columns and a search box (owner, 2026-11-04). */
function CustomerTickets({ tickets }: { tickets: TicketRowC[] }) {
  const [quick, setQuick] = useState("");
  const columns = useMemo<ColDef<TicketRowC>[]>(() => [
    { field: "ticketNo", headerName: "Ticket", width: 110, filter: "agNumberColumnFilter", sort: "desc",
      cellRenderer: (p: { value: number }) => <Link to={`/tickets/${p.value}`}>#{p.value}</Link> },
    { field: "createdAt", headerName: "Created", width: 130, filter: "agDateColumnFilter",
      valueFormatter: (p) => (p.value ? date(p.value as string) : ""),
      filterParams: { comparator: (day: Date, v: string) => { const d = new Date(v); const a = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(); return a < day.getTime() ? -1 : a > day.getTime() ? 1 : 0; } } },
    { field: "status", headerName: "Status", flex: 1, minWidth: 150, filter: "agTextColumnFilter" },
    { field: "brands", headerName: "Brands", flex: 1, minWidth: 130, filter: "agTextColumnFilter" },
    { field: "jobNumber", headerName: "Job number", width: 140, filter: "agTextColumnFilter" },
    { field: "jobName", headerName: "Job name", flex: 1, minWidth: 140, filter: "agTextColumnFilter" },
    { field: "amount", headerName: "Amount", width: 120, type: "rightAligned", filter: "agNumberColumnFilter",
      valueGetter: (p) => (p.data?.amount == null ? null : Number(p.data.amount)), valueFormatter: (p) => (p.value == null ? "—" : money(p.value as number)) },
  ], []);
  return (
    <section className="panel">
      <div className="panel-head"><h3>Tickets</h3>
        <input type="search" className="grid-search" placeholder="Search these tickets…" value={quick} onChange={(e) => setQuick(e.target.value)} aria-label="Search tickets" /></div>
      <div className="cust-grid-box">
        <AgGridReact<TicketRowC> theme={theme} rowData={tickets} columnDefs={columns} quickFilterText={quick}
          defaultColDef={{ sortable: true, resizable: true, floatingFilter: true }} getRowId={(p) => String(p.data.ticketNo)} />
      </div>
    </section>
  );
}
